import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useDocumentParams } from 'app/router/params';
import { Typography } from 'app/components';
import { TagItemShortcuts, TagAction } from '../../TagItemShortcuts/TagItemShortcuts';
import {
  useAddCurrencyToConfig,
  useRemoveCurrencyFromConfig,
  useSetActiveCurrency,
  DocumentTaxonomyConfigCurrency,
  useCreateUnit,
  useUnitsByDocument,
} from 'entities/tag';
import { AffectedTagsModal } from './modals';
import { UnitModal, UnitSpec } from 'app/components/units/UnitModal';
import { unitLabel, nonConfigUnits, simpleCurrencyCode } from 'app/utils/unit-labels';
import Tippy from '@tippyjs/react';

import { MissingDefaultHint } from './MissingDefaultHint';

import './Section.scss';

interface UnitDefaultsSectionProps {
  configId: string;
  documentSuperId: number;
  currencySelections: DocumentTaxonomyConfigCurrency[];
  currencyMissing: boolean;
}

interface DeleteTarget {
  id: string;
  currency: string;
  count: number;
}

export const UnitDefaultsSection = ({ configId, documentSuperId, currencySelections, currencyMissing }: UnitDefaultsSectionProps) => {
  const { t } = useTranslation('tag');

  const [showUnitModal, setShowUnitModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const navigate = useNavigate();
  const { documentId } = useDocumentParams();

  const { mutate: addCurrencyToConfig } = useAddCurrencyToConfig();
  const { mutate: removeCurrencyFromConfig } = useRemoveCurrencyFromConfig();
  const { mutate: setActiveCurrency } = useSetActiveCurrency();
  const { mutateAsync: createUnit } = useCreateUnit();
  const { data: documentUnits } = useUnitsByDocument(documentSuperId);

  const otherUnits = nonConfigUnits(currencySelections, documentUnits);
  const activeCurrency = currencySelections.find((c) => c.isActive)?.currency ?? null;

  const handleSave = async (spec: UnitSpec) => {
    const code = simpleCurrencyCode(spec);
    if (code) {
      if (!currencySelections.some((c) => c.currency === code)) {
        addCurrencyToConfig({ configId, currency: code });
      }
      return;
    }
    await createUnit({ documentSuperId, ...spec });
  };

  const handleDeleteClick = (monetary: DocumentTaxonomyConfigCurrency) => {
    const count = monetary.tagCount ?? 0;
    if (count > 0) {
      setDeleteTarget({ id: monetary.id, currency: monetary.currency, count });
    } else {
      removeCurrencyFromConfig({ configId, currencyId: monetary.id });
    }
  };

  const handleGoToTags = () => {
    if (!deleteTarget) return;
    navigate({
      to: '/documents/$documentId/tagging',
      params: { documentId },
      search: {
        tagFilter: { type: 'currency', value: `iso4217:${deleteTarget.currency}`, label: deleteTarget.currency },
      },
    });
    setDeleteTarget(null);
  };

  return (
    <section className="defaults-section">
      <div className="defaults-section__header">
        <div className="defaults-section__title">
          <Typography variant="body-8" fontWeight="semibold">
            {t('units.title')} {t('default.label')}
          </Typography>
          {currencyMissing && <MissingDefaultHint labelKey="defaults.missing.currency" />}
        </div>
        <button className="custom-btn" onClick={() => setShowUnitModal(true)}>
          <i className="fa-regular fa-plus" />
          {t('units.add')}
        </button>
      </div>

      {currencySelections.length === 0 && otherUnits.length === 0 ? (
        <div className="empty-state">
          <Typography variant="body-7" color="#6c757d">
            {t('units.empty')}
          </Typography>
        </div>
      ) : (
        <div className="defaults-list">
          {[...currencySelections]
            .sort((a, b) => Number(b.isActive) - Number(a.isActive))
            .map((monetary) => {
              const count = monetary.tagCount ?? 0;
              const actions: TagAction[] = [{ type: 'delete', onClick: () => handleDeleteClick(monetary) }];

              if (!monetary.isActive) {
                actions.unshift({ type: 'default', onClick: () => setActiveCurrency({ configId, currencyId: monetary.id }) });
              }

              return (
                <div key={monetary.id} className={`default-item ${monetary.isActive ? 'default-item--primary' : ''}`}>
                  <div className="default-item__main">
                    <div className="default-item__indicator">
                      {monetary.isActive && (
                        <Tippy
                          content={
                            <Typography variant="body-5" color="#6c757d" className="defaults-section__tippy-content">
                              {t('primary.monetary')}
                            </Typography>
                          }
                        >
                          <i className="fa-solid fa-star fa-xs" />
                        </Tippy>
                      )}
                    </div>

                    <div className="default-item__content">
                      <Typography variant="body-7" fontWeight="semibold">
                        {monetary.currency}
                      </Typography>
                    </div>
                  </div>
                  <div className="default-item__meta">
                    <Typography variant="body-5" color="#547BA2">
                      {count} {t('active.tags')}
                    </Typography>
                    <TagItemShortcuts actions={actions} />
                  </div>
                </div>
              );
            })}

          {otherUnits.map((unit) => (
            <div key={unit.id} className="default-item">
              <div className="default-item__main">
                <div className="default-item__indicator" />
                <div className="default-item__content">
                  <Typography variant="body-7" fontWeight="semibold">
                    {unitLabel(unit)}
                  </Typography>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <UnitModal show={showUnitModal} defaultCurrency={activeCurrency} onClose={() => setShowUnitModal(false)} onSave={handleSave} />

      <AffectedTagsModal
        show={!!deleteTarget}
        affectedCount={deleteTarget?.count ?? 0}
        itemLabel={deleteTarget?.currency ?? ''}
        onClose={() => setDeleteTarget(null)}
        onGoToTags={handleGoToTags}
      />
    </section>
  );
};
